import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useNavigate } from 'react-router-dom'
import InputField from 'components/styledFormElements/InputField'
import Tooltip from 'components/styledFormElements/Tooltip'
import { Loader } from 'components/loader'
import {
  usePurgeCache,
  useCloudFrontStatus,
  useCloudflareVerifyStatus
} from 'hooks/cache-purge'
import { useNotificationBar } from 'context/notificationContext'
import { getErrorResponse } from 'services/util'
import { useQueryClient } from 'react-query'
import { QueryKeys } from 'hooks/_query_keys'

const purgeTypeOptions = [
  { display: 'Cloudflare', value: 'cloudflare' },
  { display: 'CloudFront', value: 'cloudfront' },
]

const purgeSchema = yup.object({
  purge_type: yup
    .string()
    .required('Please select a CDN provider')
    .oneOf(['cloudflare', 'cloudfront'], 'Invalid CDN provider'),
  urls: yup
    .string()
    .required('URLs are required')
    .test('urls-required', 'At least one URL is required', (value) => {
      if (!value) return false
      const urlArray = value
        .split(/[,\n]/)
        .map((url) => url.trim())
        .filter((url) => url.length > 0)
      return urlArray.length > 0
    })
    .test('valid-urls', 'All URLs must be valid', (value) => {
      if (!value) return false
      const urlArray = value
        .split(/[,\n]/)
        .map((url) => url.trim())
        .filter((url) => url.length > 0)

      return urlArray.every((url) => {
        try {
          const urlToValidate = url.startsWith('http://') || url.startsWith('https://')
            ? url
            : `https://${url}`
          new URL(urlToValidate)
          return true
        } catch {
          return false
        }
      })
    }),
})

type PurgeFormValues = {
  purge_type: 'cloudflare' | 'cloudfront' | ''
  urls: string
}

const CachePurgeTool = () => {
  const notification = useNotificationBar()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [purgeResponse, setPurgeResponse] = useState<string>('')
  const [cloudFrontInvalidationId, setCloudFrontInvalidationId] = useState<string | null>(null)
  const [cloudflareUrlsToVerify, setCloudflareUrlsToVerify] = useState<string[] | null>(null)
  const [isWaitingForCloudflareVerification, setIsWaitingForCloudflareVerification] = useState<boolean>(false)
  const submittedUrlsRef = useRef<string[] | null>(null)
  const previousPurgeTypeRef = useRef<string>('cloudflare')
  const notificationShownRef = useRef<{ allMiss: boolean; maxAttempts: boolean }>({ allMiss: false, maxAttempts: false })

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<PurgeFormValues>({
    resolver: yupResolver(purgeSchema),
    defaultValues: {
      purge_type: 'cloudflare',
      urls: '',
    },
    mode: 'onChange',
  })

  const purgeType = watch('purge_type')
  const urls = watch('urls')

  const { mutate: purgeCache, isLoading: isPurging } = usePurgeCache({
    onSuccess: (response: any) => {
      const responseData = response?.data || response

      if (responseData?.cloudfront_response?.invalidationId) {
        const invalidationId = responseData.cloudfront_response.invalidationId
        setCloudFrontInvalidationId(invalidationId)
        setCloudflareUrlsToVerify(null)
      } else {
        setCloudFrontInvalidationId(null)
      }

      if (purgeType === 'cloudflare' && responseData?.cloudflare_response?.success && submittedUrlsRef.current) {
        const urlArray = submittedUrlsRef.current

        if (urlArray.length > 0) {
          setIsWaitingForCloudflareVerification(true)
          notificationShownRef.current = { allMiss: false, maxAttempts: false }

          setTimeout(() => {
            setIsWaitingForCloudflareVerification(false)
            setCloudflareUrlsToVerify(urlArray)
          }, 10000)
        }
      }

      const successMessage =
        responseData?.message ||
        response?.message ||
        JSON.stringify(responseData, null, 2)
      setPurgeResponse(successMessage)

      const isSuccess = responseData?.success !== false &&
        responseData?.cloudflare_response?.success !== false &&
        responseData?.cloudfront_response?.success !== false

      if (isSuccess) {
        notification.push({
          title: 'Purge Successful',
          message: 'Cache purge completed successfully',
          type: 'success',
        })
      } else {
        notification.push({
          title: 'Purge Failed',
          message: responseData?.error || responseData?.cloudflare_response.errors[0].message || response?.message || 'Cache purge failed',
          type: 'error',
        })
      }
    },
    onError: (error: any) => {
      setCloudFrontInvalidationId(null)
      setCloudflareUrlsToVerify(null)
      setIsWaitingForCloudflareVerification(false)
      notificationShownRef.current = { allMiss: false, maxAttempts: false }
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to purge cache'
      setPurgeResponse(`Error: ${errorMessage}`)
      notification.push(getErrorResponse(error))
    },
  })

  const { data: cloudFrontStatus, isLoading: isCheckingStatus } = useCloudFrontStatus(
    cloudFrontInvalidationId,
    {
      enabled: Boolean(cloudFrontInvalidationId) && purgeType === 'cloudfront',
    }
  )

  const { data: cloudflareVerifyStatus, isLoading: isVerifyingCloudflare } = useCloudflareVerifyStatus(
    cloudflareUrlsToVerify,
    {
      enabled: Boolean(cloudflareUrlsToVerify && cloudflareUrlsToVerify.length > 0) && purgeType === 'cloudflare',
    }
  )

  useEffect(() => {
    const previousType = previousPurgeTypeRef.current
    if (previousType && previousType !== purgeType && purgeType) {
      setValue('urls', '', { shouldValidate: false })
      setPurgeResponse('')
      setCloudFrontInvalidationId(null)
      setCloudflareUrlsToVerify(null)
      setIsWaitingForCloudflareVerification(false)
      submittedUrlsRef.current = null
      notificationShownRef.current = { allMiss: false, maxAttempts: false }
    }
    previousPurgeTypeRef.current = purgeType
  }, [purgeType, setValue])

  useEffect(() => {
    if (cloudFrontStatus && cloudFrontInvalidationId) {
      const statusInfo = `\n\n--- CloudFront Status ---\nStatus: ${cloudFrontStatus.status}\nInvalidation ID: ${cloudFrontInvalidationId}`

      setPurgeResponse((prev) => {
        const withoutStatus = prev.split('\n\n--- CloudFront Status ---')[0]
        return withoutStatus + statusInfo
      })
    }
  }, [cloudFrontStatus, cloudFrontInvalidationId])

  useEffect(() => {
    if (cloudflareVerifyStatus && cloudflareUrlsToVerify) {
      const results = cloudflareVerifyStatus.results || []
      const attemptCount = cloudflareVerifyStatus.attemptCount || 0
      const allMiss = results.every((result: any) => result.cacheStatus === 'MISS')
      const maxAttemptsReached = attemptCount >= 5

      let statusInfo = `\n\n--- Cloudflare Verification Status ---\n`
      statusInfo += `Attempt: ${attemptCount} of 5\n`
      results.forEach((result: any) => {
        statusInfo += `URL: ${result.url}\nCache Status: ${result.cacheStatus}\n`
      })

      if (allMiss) {
        statusInfo += `\nOverall: All URLs cleared (MISS) - Verification complete`
      } else if (maxAttemptsReached) {
        statusInfo += `\nOverall: Maximum attempts reached (5). Some URLs may still be cached (HIT).`
      } else {
        statusInfo += `\nOverall: Some URLs still cached (HIT) - Retrying...`
      }

      setPurgeResponse((prev) => {
        const withoutStatus = prev.split('\n\n--- Cloudflare Verification Status ---')[0]
        return withoutStatus + statusInfo
      })

      if (allMiss && !notificationShownRef.current.allMiss) {
        notificationShownRef.current.allMiss = true
        notification.push({
          title: 'Cache Verification Complete',
          message: 'All URLs have been successfully cleared from Cloudflare cache',
          type: 'success',
        })
      } else if (maxAttemptsReached && !notificationShownRef.current.maxAttempts) {
        notificationShownRef.current.maxAttempts = true
        notification.push({
          title: 'Verification Stopped',
          message: 'Maximum attempts reached. Some URLs may still be cached.',
          type: 'info',
        })
      }
    }
  }, [cloudflareVerifyStatus, cloudflareUrlsToVerify, notification])

  const onSubmit = useCallback((data: PurgeFormValues) => {
    const urlArray = data.urls
      .split(/[,\n]/)
      .map((url) => url.trim())
      .filter((url) => url.length > 0)

    const urlsString = urlArray.join(',')

    // Prepare API payload
    const payload = {
      purge_type: data.purge_type,
      urls: urlsString,
    }

    queryClient.removeQueries([
      QueryKeys.CACHE_PURGE,
      'cloudflare',
      'verify'
    ])
    submittedUrlsRef.current = data.purge_type === 'cloudflare' ? urlArray : null

    setPurgeResponse('')
    setCloudFrontInvalidationId(null)
    setCloudflareUrlsToVerify(null)
    setIsWaitingForCloudflareVerification(false)
    notificationShownRef.current = { allMiss: false, maxAttempts: false }

    purgeCache({ data: payload })
  }, [purgeCache, queryClient])

  const isFormValid = useMemo(() => {
    return purgeType && urls && urls.trim().length > 0
  }, [purgeType, urls])

  const isStatusInProgress = useMemo(() => {
    return cloudFrontStatus?.status === 'InProgress' || isCheckingStatus
  }, [cloudFrontStatus, isCheckingStatus])

  const isCloudflareVerifying = useMemo(() => {
    if (!cloudflareUrlsToVerify) return false

    const results = cloudflareVerifyStatus?.results || []
    const attemptCount = cloudflareVerifyStatus?.attemptCount || 0

    const allMiss = results.length > 0 && results.every((r: any) => r.cacheStatus === 'MISS')
    const maxAttemptsReached = attemptCount >= 5
    if (!allMiss && !maxAttemptsReached) {
      return true
    }

    return false
  }, [cloudflareVerifyStatus, cloudflareUrlsToVerify])

  const isAnyOperationInProgress = useMemo(() => {
    if (purgeType === 'cloudfront') {
      return isStatusInProgress
    }
    if (purgeType === 'cloudflare') {
      return isCloudflareVerifying || isWaitingForCloudflareVerification
    }
    return false
  }, [purgeType, isStatusInProgress, isCloudflareVerifying, isWaitingForCloudflareVerification])

  const handleCancel = useCallback(() => {
    navigate(-1)
  }, [navigate])

  return (
    <div className="w-full px-6 py-6 bg-gray-50 dark:bg-slate-900">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
          Cache Purge Tool
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Purge cache for Cloudflare or CloudFront CDN providers
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-6">
              Configuration
            </h4>

            <div className="space-y-6">
              <div>
                {/* <div className="flex items-center gap-2 mb-2"> */}
                  <div className="flex items-center gap-1 mb-2">
                  <label className="text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                    CDN Provider <span className="text-red-700">*</span>
                  </label>
                  <Tooltip
                    items={[
                      'Select the CDN provider for cache purging',
                      'Cloudflare: Use for Cloudflare CDN cache',
                      'CloudFront: Use for AWS CloudFront CDN cache',
                    ]}
                  />
                </div>
                <div className="flex gap-6">
                  {purgeTypeOptions.map((option) => (
                    <label key={option.value} className="flex items-center gap-2">
                      <input
                        type="radio"
                        value={option.value}
                        {...register('purge_type', { required: true })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm dark:text-white">{option.display}</span>
                    </label>
                  ))}
                </div>
                {errors.purge_type && (
                  <p className="text-xs text-red-600 mt-1">
                    {errors.purge_type.message}
                  </p>
                )}
              </div>

              <div>
                <InputField
                  label="URLs"
                  required
                  helpText="Enter one or more URLs to purge. Separate multiple URLs by comma or new line. Each URL will be purged from the selected CDN cache."
                  isTextarea
                  row={8}
                  texAreaClass="min-h-40"
                  register={register('urls', {
                    required: 'URLs are required',
                  })}
                  error={errors.urls?.message}
                  placeholder={`Enter URLs separated by comma or new line\nExample:\nhttps://example.com/page1,https://example.com/page2`}
                />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm relative">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Response
              </h4>
              {purgeType === 'cloudfront' && cloudFrontInvalidationId && (
                <div className="flex items-center gap-2">
                  {isCheckingStatus && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 animate-pulse flex items-center gap-1">
                      <Loader />
                      Checking status...
                    </span>
                  )}
                  {cloudFrontStatus && (
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded ${cloudFrontStatus.status === 'Completed'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}
                    >
                      {cloudFrontStatus.status}
                    </span>
                  )}
                </div>
              )}
              {purgeType === 'cloudflare' && cloudflareUrlsToVerify && (
                <div className="flex items-center gap-2">
                  {isVerifyingCloudflare && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 animate-pulse flex items-center gap-1">
                      <Loader />
                      Verifying cache...
                    </span>
                  )}
                  {cloudflareVerifyStatus?.results && (() => {
                    const results = cloudflareVerifyStatus.results
                    const attemptCount = cloudflareVerifyStatus.attemptCount || 0
                    const allMiss = results.every((r: any) => r.cacheStatus === 'MISS')
                    const maxAttemptsReached = attemptCount >= 5

                    let label = 'Verifying...'
                    let style =
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'

                    if (allMiss) {
                      label = 'All Cleared'
                      style =
                        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    } else if (maxAttemptsReached) {
                      label = 'Invalidated...'
                      style =
                        'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                    }

                    return (
                      <span className={`text-xs font-medium px-2 py-1 rounded ${style}`}>
                        {label}
                      </span>
                    )
                  })()}
                </div>
              )}
            </div>
            <div className="relative">
              {isAnyOperationInProgress && (
                <div className="absolute inset-0 bg-white dark:bg-slate-800 bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-10 rounded-md">
                  <div className="flex flex-col items-center gap-2">
                    <Loader />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {purgeType === 'cloudfront'
                        ? 'Checking CloudFront status...'
                        : isWaitingForCloudflareVerification
                          ? 'Waiting to verify cache status...'
                          : 'Verifying Cloudflare cache status...'}
                    </span>
                  </div>
                </div>
              )}
              <textarea
                readOnly
                value={purgeResponse}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-gray-50 dark:bg-slate-700 text-sm text-gray-900 dark:text-gray-100 min-h-[400px] resize-y focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                placeholder="Purge response will appear here..."
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4 flex justify-end gap-3 z-10">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 bg-gray-300 dark:bg-slate-700 rounded"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={!isFormValid || isPurging || isAnyOperationInProgress}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {(isPurging || isAnyOperationInProgress) && <Loader />}
            {isPurging
              ? 'Purging...'
              : isAnyOperationInProgress
                ? (purgeType === 'cloudfront' ? 'Checking Status...' : 'Verifying Cache...')
                : 'Purge'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default CachePurgeTool
